import React, { useState } from 'react';
import { X } from 'lucide-react';

interface Props {
  onClose: () => void;
  onSubmit: (data: {
    report_code: string;
    family: string;
    title: string;
    description?: string;
    category?: string;
  }) => Promise<void>;
}

export const ReportCreateModal: React.FC<Props> = ({ onClose, onSubmit }) => {
  const [reportCode, setReportCode] = useState('');
  const [family, setFamily] = useState('SAFETY');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('STANDARD');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await onSubmit({
        report_code: reportCode,
        family,
        title,
        description: description || undefined,
        category,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating report definition');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-xl">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Define Report Specification</h3>
            <p>Establish report definition template (Definition → Run → Snapshot pipeline)</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-grid-2">
              <div className="form-field">
                <label>Report Code *</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. RPT-SAF-Q1"
                  value={reportCode}
                  onChange={(e) => setReportCode(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Report Domain Family *</label>
                <select
                  value={family}
                  onChange={(e) => setFamily(e.target.value)}
                >
                  <option value="SAFETY">Safety</option>
                  <option value="QUALITY">Quality</option>
                  <option value="RISK">Risk Management</option>
                  <option value="COMPLIANCE">Compliance</option>
                  <option value="AUDIT">Audit</option>
                  <option value="MANAGEMENT">Management Review</option>
                  <option value="REGULATORY">Regulatory Authority</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>Report Title *</label>
              <input
                required
                type="text"
                placeholder="e.g. Monthly Apron Safety & Occurrence Statistical Summary"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Description & Scope of Data</label>
              <textarea
                rows={3}
                placeholder="Target stakeholder audience, underlying domain datasets, and reporting cadence..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating...' : 'Create Report Definition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
